import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { map, Observable } from 'rxjs';

import { ENV_CONFIG } from '@ps/core-config';
import { LOADING_CONTEXT } from '@ps/core-overlay';

import { Category } from '../models/category.model';
import {
  QuestionDetail,
  QuestionFormModel,
} from '../models/question-detail.model';
import { CategoryDto } from './dtos/questions.dto';
import { QuestionDetailDto } from './dtos/question-detail.dto';
import { QuestionDetailMapper } from './mappers/question-detail.mapper';
import { QuestionPayloadMapper } from './mappers/question-payload.mapper';

@Injectable({ providedIn: 'root' })
export class QuestionDetailApi {
  private readonly _http = inject(HttpClient);
  private readonly _env = inject(ENV_CONFIG);

  private readonly _detailMapper = new QuestionDetailMapper();
  private readonly _questionPayloadMapper = new QuestionPayloadMapper();

  private readonly _attributesUrl = `${this._env.pureSpectrum.url}/buyers/v3/attributes`;

  public getById(id: number): Observable<QuestionDetail> {
    return this._http
      .get<QuestionDetailDto>(`${this._attributesUrl}/${id}`, {
        params: { stats: 1, UI: 1 },
        context: new HttpContext().set(LOADING_CONTEXT, true),
      })
      .pipe(map((dto) => this._detailMapper.fromDto(dto)));
  }

  public create(
    model: QuestionFormModel,
    categories: Category[],
  ): Observable<void> {
    return this._http.post<void>(
      this._attributesUrl,
      this._questionPayloadMapper.toPayload(model, categories),
      { context: new HttpContext().set(LOADING_CONTEXT, true) },
    );
  }

  public update(
    id: number,
    model: QuestionFormModel,
    categories: Category[],
  ): Observable<void> {
    return this._http.put<void>(
      `${this._attributesUrl}/${id}`,
      this._questionPayloadMapper.toPayload(model, categories),
      { context: new HttpContext().set(LOADING_CONTEXT, true) },
    );
  }

  public delete(id: number, locale?: string): Observable<void> {
    return this._http.delete<void>(`${this._attributesUrl}/${id}`, {
      params: locale ? { localization: locale } : {},
      context: new HttpContext().set(LOADING_CONTEXT, true),
    });
  }

  public categories(): Observable<Category[]> {
    return this._http
      .get<CategoryDto[]>(`${this._attributesUrl}/categories/all`)
      .pipe(
        map((dtos) => dtos.map((dto) => ({ id: dto.id, label: dto.label }))),
      );
  }
}
